import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai, triggerShare } from "../utils/Utils";

// Shimmer helpers (same pattern as other lifestyle cards)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const LifestylePyramidSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />

          {/* Title + subtitle */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "60%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Info icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 20,
              height: 20,
              borderRadius: 999,
            }}
          />
        </div>
      </div>

      {/* Chart skeleton */}
      <div
        style={{
          marginTop: 12,
          padding: "0 16px 20px 16px",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 210,
            borderRadius: 16,
          }}
        />
      </div>
    </div>
  );
};

const LifestylePyramid = ({ shareConfig } = {}) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    const { encryptedId } = getUrlParams();
    if (!encryptedId) return;

    lifestyleApi
      .lifepyramidData(encryptedId)
      .then((response) => {
        if (response?.data?.code === 200) {
          setChartData(response.data.data);
        } else {
          console.error("Invalid response:", response);
        }
      })
      .catch((err) => console.error("Error loading data:", err));
  }, []);

  if (!chartData) return <LifestylePyramidSkeleton />;


  const foregroundData = chartData.yAxis[1].data;
  const backgroundData = chartData.yAxis[0].data;

  const maxValue = 30;

  const gapSeries = foregroundData.map((item) => ({
    y: item.y > 0 ? 0.06 : 0,
    color: "var(--bg-card)",
    enableMouseTracking: false,
    dataLabels: { enabled: false }
  }));

  const remainingSeries = foregroundData.map((item, i) => ({
    y: maxValue - item.y,
    color: backgroundData[i]?.color,
    enableMouseTracking: false,
    dataLabels: { enabled: false }
  }));

  const doneSeries = foregroundData.map((item) => ({
    y: item.y,
    color: item.y > 0 ? item.color : "transparent",
    mystring: item.mystring
  }));

  const options = {
    chart: {
      type: "bar",
      backgroundColor: "var(--bg-card)",
      height: 155,
      borderRadius: 20,
      spacingTop: 0,
      spacingLeft: 10,
      spacingRight: 10,


    },

    title: { text: null },

    xAxis: {
      categories: chartData.xAxis,
      title: null,
      labels: { style: { fontSize: "9px", fontFamily: "Open Sans, sans-serif", color: "var(--text-secondary)" } }
    },

    yAxis: {
      max: 30,
      title: null,
      visible: false
    },

    legend: { enabled: false },
    tooltip: { enabled: false },

    plotOptions: {
      series: {
        stacking: "normal",
        borderWidth: 0,
        borderRadius: 0,
        groupPadding: 0,
        pointPadding: 0,
        pointWidth: 30,
        minPointLength: 2,

        allowPointSelect: false,
        states: {
          hover: { enabled: false },
          select: { enabled: false },
          inactive: { enabled: false }
        },
        dataLabels: {
          enabled: true,
          inside: true,
          align: "right",
          formatter: function () {
            return this.point.mystring
              ? `${this.point.y} ${this.point.mystring}`
              : "";
          },
          style: {
            fontWeight: "bold",
            fontFamily: "Open Sans, sans-serif",
            color: "var(--text-primary)"
          }
        }
      }
    },

    series: [
      { name: "Remaining", data: remainingSeries },
      { name: "Gap", data: gapSeries },
      { name: "Done", data: doneSeries }
    ],

    credits: { enabled: false }
  };

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/89295_1764052429.png"
        title={chartData.title}
        subtitle={chartData.heading}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        showInfo
        onInfoClick={() => {
          navigateWithFai("My Health Awareness", chartData.infoUrl);
        }}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />

      <HighchartsReact highcharts={Highcharts} options={options} />
      <div
        style={{
          padding: "8px 16px",
          textAlign: "left",
          fontSize: "9px",
          color: "var(--text-secondary)",
          fontFamily: "Open Sans, sans-serif",
        }}
      >
        {chartData.monthText}
      </div>
    </div>
  );
};

export default LifestylePyramid;
